import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage all Gantt chart event handlers and interceptors
 * Provides a clean separation of event handling logic from component rendering
 */
const useGanttEventHandlers = ({
  ganttApi,
  onTaskSelect,
}) => {
  const lastZoomTime = useRef(0);
  const lastClickTime = useRef(0);
  const lastClickedTaskId = useRef(null);
  
  // Fixed configuration for EMI Gantt
  const ZOOM_THROTTLE_MS = 50;
  const DOUBLE_CLICK_THRESHOLD_MS = 300;
  const MIN_CLICK_INTERVAL_MS = 100; // Minimum time between clicks to prevent duplicate events

  useEffect(() => {
    if (!ganttApi) return;

    const cleanupFunctions = [];

    // Zoom throttling interceptor
    const unsubscribeZoomIntercept = ganttApi.intercept("zoom-scale", () => {
      const now = Date.now();
      const timeSinceLastZoom = now - lastZoomTime.current;

      if (timeSinceLastZoom < ZOOM_THROTTLE_MS) {
        return false;
      }

      lastZoomTime.current = now;
      return true;
    });

    // Zoom event observer (for future use if needed)
    const unsubscribeZoomObserve = ganttApi.on("zoom-scale", () => {
      // Future: Could add zoom level tracking or other zoom-related logic here
    });

    // Task selection handler with double-click requirement
    const unsubscribeTaskSelect = ganttApi.on("select-task", (ev) => {
      if (!onTaskSelect) return;

      const now = Date.now();
      const timeSinceLastClick = now - lastClickTime.current;
      const isSameTask = lastClickedTaskId.current === ev.id;

      // Require double-click to open editor with minimum interval to prevent duplicate events
      if (isSameTask && 
          timeSinceLastClick >= MIN_CLICK_INTERVAL_MS && 
          timeSinceLastClick <= DOUBLE_CLICK_THRESHOLD_MS) {
        onTaskSelect(ev.id);
        // Reset to prevent triple-click issues
        lastClickTime.current = 0;
        lastClickedTaskId.current = null;
      } else if (timeSinceLastClick < MIN_CLICK_INTERVAL_MS && isSameTask) {
        // Ignore rapid duplicate events - don't update lastClickTime
      } else {
        lastClickTime.current = now;
        lastClickedTaskId.current = ev.id;
      }
    });

    // Always disable these features for EMI Gantt
    const unsubscribeEditorIntercept = ganttApi.intercept("show-editor", () => false);
    const unsubscribeDragIntercept = ganttApi.intercept("drag-task", () => false);
    const unsubscribeSortIntercept = ganttApi.intercept("sort-tasks", () => false);
    const unsubscribeAddIntercept = ganttApi.intercept("add-task", () => false);
    
    // Disable mouse wheel zooming - force users to use zoom buttons
    const unsubscribeWheelZoomIntercept = ganttApi.intercept("wheel-zoom", () => false);

    cleanupFunctions.push(
      unsubscribeZoomIntercept,
      unsubscribeZoomObserve,
      unsubscribeTaskSelect,
      unsubscribeEditorIntercept,
      unsubscribeDragIntercept,
      unsubscribeSortIntercept,
      unsubscribeAddIntercept,
      unsubscribeWheelZoomIntercept
    );

    return () => {
      cleanupFunctions.forEach(cleanup => {
        if (cleanup && typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, [ganttApi, onTaskSelect]);
};

export default useGanttEventHandlers;
